import React, { useState, useRef, useEffect } from "react";
import { formatToLocalDateTime } from "../utils/formatDate";
import Loader from "../../../components/Loader";
import { CustomModal } from "./CustomModal";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import URL_MAPPING from "../../../routes/constants";
import { removeEmployeeAssignments, removeSalesOfficeAssignment } from "../api/territory";

interface LMDTerritoryTableProps {
  tableData: any;
  loading: boolean;
  refetch: boolean;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
}

const LMDTerritoryTable: React.FC<LMDTerritoryTableProps> = ({
  tableData,
  loading,
  refetch,
  setRefetch,
}) => {
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const toggleMenu = (id: string) => {
    if (id) setMenuOpen(id);
  };
  const navigate = useNavigate();

  const handleModalActivity = () => {
    setIsOpen(!isOpen);
  };

  const handleEditTerritory = () => {
    if (menuOpen === null) return;
    // Get territory_id from the row data
    const item = tableData?.items?.find((item: any) => item.id === menuOpen);
    if (item?.territory_id) {
      navigate(`${URL_MAPPING.ASSIGNLMDTERRITORY}?territory_id=${item.territory_id}`);
    }
  };

  const handleDeleteTerritory = async () => {
    try {
      if (!selectedItem) {
        toast.error("No item selected for deletion");
        return;
      }

      const { territory_id, emp_id, sales_office_id } = selectedItem;
      
      // Determine which API to call based on the row type
      if (emp_id && emp_id !== "-") {
        // This is an employee assignment - call remove-employee-assignments
        const payload = {
          territory_id: String(territory_id),
          emp_ids: [String(emp_id)],
        };
        const response = await removeEmployeeAssignments(payload);
        if (!response) {
          toast.error("No data received or malformed response");
          return;
        }
        toast.success(response?.message || "Employee assignment removed successfully");
      } else if (sales_office_id && sales_office_id !== "-") {
        // This is a sales office assignment - call remove-sales-office-assignment
        const payload = {
          territory_id: territory_id,
          sales_office_ids: [String(sales_office_id)],
        };
        const response = await removeSalesOfficeAssignment(payload);
        if (!response) {
          toast.error("No data received or malformed response");
          return;
        }
        toast.success(response?.message || "Sales office assignment removed successfully");
      } else {
        toast.error("Invalid item selected for deletion");
        return;
      }

      handleModalActivity();
      setRefetch(!refetch);
    } catch (error: any) {
      console.error("Failed to delete assignment:", error);
      if (error?.response?.status === 404) {
        toast.error("Route not found, Please try again later");
      } else if (error?.response?.status === 500) {
        toast.error("Error while removing the assignment");
      } else {
        const detail = error?.response?.data?.detail;
        if (Array.isArray(detail)) {
          detail.forEach((err) => toast.error(err.msg));
        } else {
          toast.error(detail?.msg || detail || "An unexpected error occurred.");
        }
      }
    }
  };
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setMenuOpen(null); // Close menu
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  return (
    <>
      <ToastContainer />
      <CustomModal
        isOpen={isOpen}
        onClose={handleModalActivity}
        onDelete={handleDeleteTerritory}
      />
      <div className="h-[calc(100vh-200px)] md:h-[calc(100vh-200px)] overflow-auto page-container p-2 pt-0 scrollbar-thin">
        <table className="min-w-full bg-white border border-black-200 table-auto border-separate border-spacing-0">
          <thead className="bg-gray-200 sticky top-[0px] z-20">
            <tr>

              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap w-28">
                Emp ID
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 min-w-[200px]">
                Emp Name
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap w-36">
                Sales Office ID
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 min-w-[200px]">
                Sales Office Name
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap w-40">
                Territory ID
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 min-w-[200px]">
                Territory Name
              </th>

              <th className="border border-gray-300 text-13 font-bold text-black-500 whitespace-nowrap px-2 py-2 w-20">
                Actions
              </th>
            </tr>
          </thead>
          {loading ? (
            <tbody>
              <tr>
                <td colSpan={8} className="py-8">
                  <div className="flex justify-center items-center w-full">
                    <Loader />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : !loading && tableData?.items?.length === 0 ? (
            <tr>
              <td colSpan={8} className="py-8 text-center text-gray-500">
                No results found
              </td>
            </tr>
          ) : (
            <tbody>
              {Array.isArray(tableData?.items) &&
                tableData.items.map((item: any, index: number) => (
                  <tr key={index} className="border-b text-sm">
                   
                    <td className="border border-gray-300 px-4 py-2 whitespace-nowrap">
                      {item.emp_id || "-"}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 break-words">
                      {item.emp_name || item.employee_name || "-"}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 whitespace-nowrap">
                      {item.sales_office_id || "-"}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 break-words">
                      {item.sales_office_name || "-"}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 whitespace-nowrap">
                      {item.territory_id || "-"}
                    </td>
                    <td className="border border-gray-300 px-4 py-2 break-words">
                      {item.territory_name || "-"}
                    </td>

                    <td className="border border-gray-300 px-4 py-2 text-center relative">
                      <button
                        onClick={() => toggleMenu(item.id)}
                        className="text-gray-500 hover:text-gray-800"
                      >
                        <p className="text-lg">⋮</p>
                      </button>
                      {menuOpen === item.id && (
                        <div
                          ref={dropdownRef}
                          className={`absolute right-10 -mt-5 w-32 bg-white border border-gray-200 shadow-lg rounded z-10 text-left`}
                        >
                          <ul className="py-1 text-sm text-gray-900">
                            <li
                              className="px-4 py-2 hover:bg-gray-200 cursor-pointer"
                              onClick={() => handleEditTerritory()}
                            >
                              Edit
                            </li>
                            <li
                              className="px-4 py-2 hover:bg-gray-200 cursor-pointer"
                              onClick={() => {
                                setSelectedItem(item); // set the entire item for modal
                                handleModalActivity();
                              }}
                            >
                              Delete
                            </li>
                          </ul>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          )}
        </table>
      </div>
    </>
  );
};

export default LMDTerritoryTable;

